import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

const PILLAR_COLORS = {
  'Precision Weight Management & Metabolic Health': 'bg-amber-50 text-amber-700',
  'Longevity & Biological Age': 'bg-emerald-50 text-emerald-700',
  'Hormones Across the Life Course': 'bg-violet-50 text-violet-700',
  'Microbiome, Genetics & Multi-Omics': 'bg-sky-50 text-sky-700',
  'Aesthetics with a Physician\u2019s Perspective': 'bg-rose-50 text-rose-700',
  "Aesthetics with a Physician's Perspective": 'bg-rose-50 text-rose-700',
  'Functional & Lifestyle Medicine': 'bg-stone-100 text-stone-700',
};

const formatPublished = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const RecentArticles = ({ posts: postsProp }) => {
  const [internalPosts, setInternalPosts] = useState(null);

  useEffect(() => {
    if (postsProp !== undefined) return; // parent owns the data
    api('/api/blog')
      .then((data) => setInternalPosts(Array.isArray(data) ? data.slice(0, 3) : []))
      .catch(() => setInternalPosts([]));
  }, [postsProp]);

  const posts = postsProp !== undefined ? postsProp : internalPosts;
  const hasPosts = posts && posts.length > 0;

  return (
    <section
      id="recent-articles"
      className="py-24 bg-white border-t border-stone-100"
      data-testid="recent-articles-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-rose-400" />
              <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">
                From the Journal
              </span>
            </div>
            <h2
              className="font-serif text-3xl lg:text-4xl text-stone-900 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Latest from our physicians
            </h2>
            <p className="text-stone-500 mt-3 font-light leading-relaxed">
              Evidence-based perspectives on aesthetics, longevity, hormones,
              and precision health &mdash; written by the team at Akira.
            </p>
          </div>
          <Link
            to="/articles"
            data-testid="recent-articles-view-all"
            className="group inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 text-sm tracking-widest uppercase self-start md:self-end whitespace-nowrap"
          >
            View all articles
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Cards */}
        {hasPosts ? (
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((p) => {
              const pillarClass = PILLAR_COLORS[p.pillar] || 'bg-stone-100 text-stone-700';
              return (
                <Link
                  key={p.id}
                  to={`/articles/${p.slug}`}
                  data-testid={`recent-article-${p.slug}`}
                  className="group block bg-white border border-stone-200 hover:border-rose-300 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500 ease-out shadow-sm overflow-hidden"
                >
                  {p.cover_image ? (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-stone-100 via-rose-50 to-stone-50 flex items-center justify-center">
                      <Sparkles className="w-10 h-10 text-rose-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {p.pillar && (
                        <span
                          className={`inline-block text-[10px] tracking-[0.25em] uppercase px-2.5 py-1 ${pillarClass}`}
                        >
                          {p.pillar}
                        </span>
                      )}
                      {p.created_at && (
                        <time
                          dateTime={new Date(p.created_at).toISOString()}
                          className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-stone-500 px-2 py-1 bg-stone-50"
                          data-testid={`recent-article-date-${p.slug}`}
                        >
                          <Calendar className="w-3 h-3" />
                          {formatPublished(p.created_at)}
                        </time>
                      )}
                    </div>
                    <h3
                      className="font-serif text-xl text-stone-900 mb-3 leading-snug group-hover:text-rose-500 transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {p.title}
                    </h3>
                    <p className="text-stone-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {p.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-100 text-xs">
                      <div className="text-stone-500">
                        <div className="font-medium text-stone-800">{p.author_name}</div>
                        {p.reading_time_min ? (
                          <div className="flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {p.reading_time_min} min read
                          </div>
                        ) : null}
                      </div>
                      <ArrowRight className="w-4 h-4 text-rose-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          // Skeleton while loading; vanishes once posts arrive.
          posts === null && (
            <div className="grid md:grid-cols-3 gap-8" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-white border border-stone-200 overflow-hidden">
                  <div className="h-48 bg-stone-100 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-24 bg-stone-100 animate-pulse" />
                    <div className="h-5 w-3/4 bg-stone-100 animate-pulse" />
                    <div className="h-3 w-full bg-stone-100 animate-pulse" />
                    <div className="h-3 w-5/6 bg-stone-100 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default RecentArticles;
